import React, { useState, useEffect, useCallback } from 'react';

import './Pagination.css';

export default function Pagination(props) {
    const [numberOfPages, setNumberOfPages] = useState([1]);
    let selectedPage = props.selectedPage;
    let pageSize = props.pageSize;
    const type = props.type;

    const range = (start, end) => {
        let length = end - start + 1;
        return Array.from({ length }, (_, x) => x + start);
    };

    const buildPaginationOptions = useCallback(() => {
        const totalNumPages = Math.ceil(props.numberOfItems / pageSize);
        const numPagesNextToSelected = 1;
        const totalPageNumbers = numPagesNextToSelected + 6;
        const SEPARATOR = "...";

        if (selectedPage > totalNumPages) {
            props.setSelectedPage(totalNumPages);
        }

        if (totalPageNumbers >= totalNumPages) {
            return range(1, totalNumPages);
        }

        const leftSiblingIndex = Math.max(selectedPage - numPagesNextToSelected, 1);
        const rightSiblingIndex = Math.min(selectedPage + numPagesNextToSelected, totalNumPages);

        const shouldShowLeftSeparator = leftSiblingIndex > 3;
        const shouldShowRightSeparator = rightSiblingIndex < (totalNumPages - 2);

        const firstPageIndex = 1;
        const lastPageIndex = totalNumPages;

        if (!shouldShowLeftSeparator && shouldShowRightSeparator) {
            let leftItemCount = 3 + (2 * numPagesNextToSelected);
            let leftRange = range(1, leftItemCount);

            return [...leftRange, SEPARATOR, totalNumPages];
        }

        if (shouldShowLeftSeparator && !shouldShowRightSeparator) {
            let rightItemCount = 3 + (2 * numPagesNextToSelected);
            let rightRange = range(
                totalNumPages - rightItemCount + 1,
                totalNumPages
            );
            return [firstPageIndex, SEPARATOR, ...rightRange];
        }

        if (shouldShowLeftSeparator && shouldShowRightSeparator) {
            let middleRange = range(leftSiblingIndex, rightSiblingIndex);
            return [firstPageIndex, SEPARATOR, ...middleRange, SEPARATOR, lastPageIndex];
        }

        return [1];
    }, [pageSize, selectedPage, props]);

    useEffect(() => {
        if (props.numberOfItems) {
            setNumberOfPages(buildPaginationOptions());
        }
    }, [selectedPage, pageSize, props.numberOfItems, buildPaginationOptions]);

    function handlePageSizeSelection(selectionValue) {
        props.setPageSize(selectionValue);
    }

    function handlePageSelection(page) {
        props.setSelectedPage(page);
    }

    return (
        <div className="table-control efup-pagination-wrapper cb-font-poppins" data-testid={`efup-pagination-wrapper-${type}`}>
            {!props.hidePageSize ? <div className="efup-pagination-page-size">
                <ul className="nav nav-pills align-items-center">
                    <li className="nav-item" key={`efup-pagination-label-${type}`}>
                        Payments per page
                    </li>
                    <li className="nav-item" key={`efup-pagination-page-size-25-${type}`}>
                        <button
                            className={`nav-link ${pageSize === 25 ? "active" : ""}`}
                            onClick={() => handlePageSizeSelection(25)}
                            data-testid={`efup-pagination-page-size-25-${type}`}
                        >25</button>
                    </li>
                    <li className="nav-item" key={`efup-pagination-page-size-50-${type}`}>
                        <button
                            className={`nav-link ${pageSize === 50 ? "active" : ""}`}
                            onClick={() => handlePageSizeSelection(50)}
                            data-testid={`efup-pagination-page-size-50-${type}`}
                        >50</button>
                    </li>
                    <li className="nav-item" key={`efup-pagination-page-size-100-${type}`}>
                        <button
                            className={`nav-link ${pageSize === 100 ? "active" : ""}`}
                            onClick={() => handlePageSizeSelection(100)}
                            data-testid={`efup-pagination-page-size-100-${type}`}
                        >100</button>
                    </li>
                    <li className="nav-item" key={`efup-pagination-page-size-200-${type}`}>
                        <button
                            className={`nav-link ${pageSize === 200 ? "active" : ""}`}
                            onClick={() => handlePageSizeSelection(200)}
                            data-testid={`efup-pagination-page-size-200-${type}`}
                        >200</button>
                    </li>
                </ul>
            </div> : <React.Fragment />}
            <div className="efup-pagination-page-number">
                <nav aria-label="Page navigation example">
                    <ul className="pagination pagination-sm justify-content-end">
                        <li className={`page-item ${selectedPage === 1 ? "disabled" : ""}`} key={`efup-pagination-left-arrow-${type}`}>
                            <button
                                className="page-link"
                                aria-label="Previous"
                                disabled={selectedPage === 1}
                                onClick={() => handlePageSelection(selectedPage - 1)}
                                data-testid={`efup-pagination-left-arrow-${type}`}
                            >
                                <span aria-hidden="true">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                        fill="currentColor" className="bi bi-chevron-left" viewBox="0 0 16 16">
                                        <path fillRule="evenodd"
                                            d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
                                    </svg>
                                </span>
                            </button>
                        </li>
                        {numberOfPages.map((item, index) => {
                            if (item === "...") {
                                return <li className="page-item disabled" key={`efup-pagination-page-${index === 1 ? "left" : "right"}-separator-${type}`}>
                                    <button
                                        className="page-link"
                                        disabled
                                        data-testid={`efup-pagination-page-${index === 1 ? "left" : "right"}-separator-${type}`}>{item}</button>
                                </li>
                            }
                            return <li className={`page-item ${item === selectedPage ? "active" : ""}`} key={`efup-pagination-page-${item}-${type}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageSelection(item)}
                                    data-testid={`efup-pagination-page-${item}-${type}`}
                                >{item}</button>
                            </li>
                        })}
                        <li
                            className={`page-item ${selectedPage === numberOfPages[numberOfPages.length - 1] ? "disabled" : ""}`}
                            key={`efup-pagination-right-arrow-${type}`}
                        >
                            <button
                                className="page-link"
                                aria-label="Next"
                                disabled={selectedPage === numberOfPages[numberOfPages.length - 1]}
                                onClick={() => handlePageSelection(selectedPage + 1)}
                                data-testid={`efup-pagination-right-arrow-${type}`}
                            >
                                <span aria-hidden="true">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                        fill="currentColor" className="bi bi-chevron-right" viewBox="0 0 16 16">
                                        <path fillRule="evenodd"
                                            d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                                    </svg>
                                </span>
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    );
}